import { readonly, type Ref } from 'vue';

type UseAuthReturn = ReturnType<typeof useAuth>;
type UseAuthStateReturn = ReturnType<typeof useAuthState>;

type Login = (email: string, password: string) => ReturnType<UseAuthReturn['signIn']>;

type UseAuthUtilsReturn = UseAuthReturn & {
    state: UseAuthStateReturn;
    login: Login;
    logout: () => void;
    isLoggedIn: Readonly<Ref<boolean>>;
};

export const useAuthUtils = (): UseAuthUtilsReturn => {
    const auth = useAuth();
    const authState = useAuthState();
    const { clearToken, data: stateData, token } = useAuthState();

    const login: Login = async (email, password) => {
        try {
            if (isLoggedIn.value) return;

            return await auth.signIn({ email, password });
        } catch (error) {
            // TODO: quick fix to catch successful login with broken redirect by nuxt-auth.
            //  For the whole crazy story see https://adorea.atlassian.net/browse/BSD-440
            if (!isLoggedIn.value) throw error;
        }
    };

    const logout = (): void => {
        clearToken();
        stateData.value = null;
    };

    const isLoggedIn = computed(() => {
        if (auth.status.value === 'authenticated') return true;
        else if (auth.status.value === 'unauthenticated') return false;

        return auth.status.value === 'loading' && !!token.value;
    });

    return {
        ...auth,
        state: authState,
        login,
        logout,
        isLoggedIn: readonly(isLoggedIn),
    };
};
